import React, { useEffect } from "react";
import { toast } from "sonner";
import { Mic } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

/**
 * Slice R⁺⁺⁺⁺⁺ · Native-language voice-mode discovery toast.
 *
 * On first visit — after the LanguageAutoDetect banner has done its
 * work — we drop one friendly toast in the user's native language telling
 * them AINA now understands and speaks their tongue. Recorded once via
 * `f2n_voice_prompt_seen` so it never nags.
 *
 * Non-goal: pushing users to use voice. We just *tell* them the loop
 * exists, then get out of the way.
 */
const NATIVE_HINTS = {
  hi: { hello: "नमस्ते",         cta: "AINA अब हिंदी में सुन और बोल सकती है — माइक दबाकर आज़माएँ।" },
  ta: { hello: "வணக்கம்",         cta: "AINA இப்போது தமிழில் கேட்டு பேசும் — மைக்கை அழுத்தி முயற்சிக்கவும்." },
  te: { hello: "నమస్తే",          cta: "AINA ఇప్పుడు తెలుగులో వినగలదు, మాట్లాడగలదు — మైక్‌ నొక్కి ప్రయత్నించండి." },
  bn: { hello: "নমস্কার",         cta: "AINA এখন বাংলায় শুনতে ও বলতে পারে — মাইক চাপ দিয়ে দেখুন।" },
  mr: { hello: "नमस्कार",         cta: "AINA आता मराठीत ऐकू व बोलू शकते — माईक दाबून पहा." },
  gu: { hello: "નમસ્તે",           cta: "AINA હવે ગુજરાતીમાં સાંભળી અને બોલી શકે છે — માઇક દબાવો." },
  kn: { hello: "ನಮಸ್ಕಾರ",       cta: "AINA ಈಗ ಕನ್ನಡದಲ್ಲಿ ಕೇಳಿ ಮಾತನಾಡುತ್ತದೆ — ಮೈಕ್ ಒತ್ತಿ." },
  ml: { hello: "നമസ്കാരം",     cta: "AINA ഇപ്പോൾ മലയാളത്തിൽ കേൾക്കാനും സംസാരിക്കാനും കഴിയും." },
  pa: { hello: "ਸਤ ਸ੍ਰੀ ਅਕਾਲ", cta: "AINA ਹੁਣ ਪੰਜਾਬੀ ਵਿੱਚ ਸੁਣ ਤੇ ਬੋਲ ਸਕਦੀ ਹੈ — ਮਾਈਕ ਦਬਾਓ।" },
  ur: { hello: "السلام علیکم",   cta: "AINA اب اردو میں سن اور بول سکتی ہے — مائیک دبائیں۔" },
  vi: { hello: "Xin chào",       cta: "AINA giờ có thể nghe và nói tiếng Việt — hãy bấm mic." },
  th: { hello: "สวัสดี",           cta: "AINA ตอนนี้พูดและฟังภาษาไทยได้ — แตะไมค์ลอง" },
  id: { hello: "Halo",            cta: "AINA sekarang bisa mendengar dan berbicara Bahasa Indonesia — coba mic." },
  ms: { hello: "Salam",           cta: "AINA kini boleh mendengar dan bertutur dalam Bahasa Melayu — cuba mikrofon." },
  tl: { hello: "Kumusta",         cta: "Nakakapagsalita at nakakarinig na si AINA ng Tagalog — pindutin ang mic." },
  zh: { hello: "你好",             cta: "AINA 现在能听懂并说中文 — 点击麦克风试试。" },
  ja: { hello: "こんにちは",       cta: "AINAは日本語で聞いて話せるようになりました。マイクを押してみてください。" },
  ko: { hello: "안녕하세요",       cta: "AINA가 이제 한국어로 듣고 말할 수 있어요. 마이크를 눌러보세요." },
  ar: { hello: "مرحبا",            cta: "يمكن لـ AINA الآن الاستماع والتحدث بالعربية — انقر على الميكروفون." },
  es: { hello: "Hola",            cta: "AINA ahora te escucha y responde en español — pulsa el micrófono." },
  fr: { hello: "Bonjour",         cta: "AINA écoute et parle maintenant en français — appuyez sur le micro." },
  pt: { hello: "Olá",             cta: "AINA agora ouve e fala em português — toque no microfone." },
  de: { hello: "Hallo",           cta: "AINA versteht und spricht jetzt Deutsch — Mikrofon antippen." },
  ru: { hello: "Здравствуйте",   cta: "AINA теперь слышит и говорит по-русски — нажмите микрофон." },
};

function _short(s) {
  return (s || "").toLowerCase().split(/[-_]/)[0].slice(0, 2);
}

export default function NativeVoicePrompt() {
  const { lang } = useI18n();

  useEffect(() => {
    // Show at most once per browser install.
    try {
      if (localStorage.getItem("f2n_voice_prompt_seen") === "1") return;
    } catch { return; }

    // Prefer the user's UI language (they may have already switched), else
    // fall back to the browser default.
    const code = _short(lang) || _short(typeof navigator !== "undefined" ? navigator.language : "");
    if (!code || code === "en") return;
    const hint = NATIVE_HINTS[code];
    if (!hint) return;

    // Wait a beat so we don't collide with the LanguageAutoDetect toast on
    // the very first paint (Slice O uses `duration: 12000`).
    const t = setTimeout(() => {
      toast(`${hint.hello} 👋`, {
        description: hint.cta,
        duration: 14000,
        icon: React.createElement(Mic, { size: 14 }),
        action: {
          label: "OK",
          onClick: () => {
            try { localStorage.setItem("f2n_voice_prompt_seen", "1"); } catch { /* ignore */ }
          },
        },
        onDismiss: () => {
          try { localStorage.setItem("f2n_voice_prompt_seen", "1"); } catch { /* ignore */ }
        },
        onAutoClose: () => {
          try { localStorage.setItem("f2n_voice_prompt_seen", "1"); } catch { /* ignore */ }
        },
      });
    }, 4000);
    return () => clearTimeout(t);
  }, [lang]);

  return null;
}
